import { useEffect, useMemo, useState } from 'react';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import inductionService from '../../services/induction';
import { errorMessage } from '../../utils/apiResponse';
import type { EquipmentInduction, PersonRef } from '../../types/induction';
import { personLabel } from './inductionLabels';

interface Props {
  person: { ref: PersonRef; name: string | null };
  /** Inductions they already hold a current sign-off for; not offered. */
  held: Set<number>;
  onClose: () => void;
  onDone: (count: number) => void;
}

type Basis = 'assessed' | 'prior_competency';

/** Sign one person off on several inductions. */
export default function PersonSignOffDialog({ person, held, onClose, onDone }: Props) {
  const [inductions, setInductions] = useState<EquipmentInduction[] | null>(null);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [basis, setBasis] = useState<Basis>('assessed');
  const [expiresOn, setExpiresOn] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    inductionService.list()
      .then(l => setInductions(l?.inductions ?? []))
      .catch(e => { setInductions([]); setError(errorMessage(e, 'Could not load inductions')); });
  }, []);

  const offered = useMemo(() => (inductions ?? []).filter(i => i.active && !held.has(i.id)), [inductions, held]);

  const toggle = (id: number) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await inductionService.signOff({
        ref: person.ref, induction_ids: [...picked], basis, notes: notes.trim() || undefined, expires_on: expiresOn || null,
      });
      onDone(picked.size);
    } catch (e) {
      setError(errorMessage(e, 'Could not record the sign-off'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      title="Sign off on…"
      onClose={onClose}
      onSubmit={submit}
      submitLabel={picked.size > 1 ? `Sign off on ${picked.size}` : 'Sign off'}
      submitting={saving}
      submitDisabled={picked.size === 0}
      error={error}
    >
      <p className="text-sm text-neutral-600 break-words"><strong>{personLabel(person.name)}</strong></p>
      <div className="max-h-56 overflow-y-auto rounded-lg border border-neutral-200 divide-y divide-neutral-100">
        {inductions === null && <p className="p-3 text-sm text-neutral-500">Loading…</p>}
        {inductions !== null && offered.length === 0 && (
          <p className="p-3 text-sm text-neutral-500">They hold every induction already.</p>
        )}
        {offered.map(i => (
          <label key={i.id} className="flex items-start gap-2 px-3 py-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-0.5 shrink-0" checked={picked.has(i.id)} onChange={() => toggle(i.id)} />
            <span className="min-w-0 break-words">
              {i.title}
              {i.equipment_name && <span className="text-neutral-400"> · {i.equipment_name} only</span>}
            </span>
          </label>
        ))}
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-neutral-700 mb-1">Basis</legend>
        <label className="flex items-start gap-2 text-sm">
          <input type="radio" name="person-basis" className="mt-1 shrink-0" checked={basis === 'assessed'} onChange={() => setBasis('assessed')} />
          <span className="min-w-0 break-words">Assessed</span>
        </label>
        <label className="flex items-start gap-2 text-sm">
          <input type="radio" name="person-basis" className="mt-1 shrink-0" checked={basis === 'prior_competency'} onChange={() => setBasis('prior_competency')} />
          <span className="min-w-0 break-words">Existing operator — recognised</span>
        </label>
      </fieldset>
      <Field label="Expires on (optional)" htmlFor="person-expires" hint="Leave blank to use each induction's refresher period.">
        <input id="person-expires" type="date" className={inputClass} value={expiresOn} onChange={e => setExpiresOn(e.target.value)} />
      </Field>
      <Field label="Notes" htmlFor="person-notes">
        <textarea id="person-notes" className={inputClass} rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
      </Field>
    </FormModal>
  );
}
